import React, { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '@/store/actions';
import { setPropertyActiveTab } from '@/store/properties-slice';
import { usePropertyMetricsQuery } from '@/hooks/queries/usePropertyDashboard';
import { usePropertyQuery } from '@/hooks/queries/useProperties';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowLeft,
  Building,
  MapPin,
  Star,
  Users,
  Calendar,
  BarChart3,
  FileText,
  ArrowRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import TrendChart from '@/components/dashboard/trend-chart';
import { AssessmentsTab } from './components/assessments-tab';
import { DepartmentsTab } from './components/departments-tab';
import { OverviewTab } from './components/overview-tab';
import { ContactTab } from './components/contact-tab';

export default function PropertyDetailsPage() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);
  const activeTab = useAppSelector(state => state.properties.activePropertyTab);

  // Use React Query hooks
  const { data: propertyData, isLoading: isLoadingProperty } = usePropertyQuery(
    id || ''
  );
  const { data: metrics, isLoading: isLoadingMetrics } =
    usePropertyMetricsQuery(id || '');

  // Extract property from response (PropertyDetailResponse is already the property object)
  const property = propertyData || null;

  // Show loading state while fetching property
  if (isLoadingProperty) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('loading')}</p>
        </div>
      </div>
    );
  }

  // Show not found message if property doesn't exist
  if (!property) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <div className={cn('text-center', isRTL ? 'text-right' : 'text-left')}>
          <h1 className="text-2xl font-bold text-muted-foreground">
            {t('properties.details.propertyNotFound')}
          </h1>
          <p className="text-muted-foreground">
            {t('properties.details.propertyNotFoundDesc')}
          </p>
        </div>
        <Button asChild>
          <Link to="/properties">
            <ArrowLeft className={cn('h-4 w-4', isRTL ? 'ml-2' : 'mr-2')} />
            {t('properties.details.backToProperties')}
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/properties">
            {isRTL ? (
              <ArrowRight className={cn('h-4 w-4', isRTL ? 'mr-2' : 'ml-2')} />
            ) : (
              <ArrowLeft className={cn('h-4 w-4', isRTL ? 'ml-2' : 'mr-2')} />
            )}
            {t('properties.details.backToProperties')}
          </Link>
        </Button>

        <div>
          <h1 className="text-3xl font-bold">{property.name}</h1>
          <div className={cn('flex items-center gap-4 mt-2 justify-center')}>
            <div className="flex items-center text-muted-foreground">
              <Building className={cn('h-4 w-4', isRTL ? 'ml-1' : 'mr-1')} />
              <span className="capitalize">
                {property.property_type?.name ||
                  t('properties.details.propertyType')}
              </span>
            </div>
            {property.stars && (
              <div className="flex items-center text-muted-foreground">
                <Star
                  className={cn(
                    'h-4 w-4 fill-current',
                    isRTL ? 'ml-1' : 'mr-1'
                  )}
                />
                <span>
                  {property.stars} {t('properties.details.stars')}
                </span>
              </div>
            )}
            {property.location && (
              <div className="flex items-center text-muted-foreground">
                <MapPin className={cn('h-4 w-4', isRTL ? 'ml-1' : 'mr-1')} />
                <span>{property.location}</span>
              </div>
            )}
            <Badge variant={property.is_active ? 'default' : 'secondary'}>
              {property.is_active
                ? t('properties.active')
                : t('properties.inactive')}
            </Badge>
          </div>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('properties.details.metrics.currentScore')}
            </CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoadingMetrics ? (
              <div className="text-2xl font-bold text-muted-foreground">--</div>
            ) : (
              <>
                <div className="text-2xl font-bold">
                  {metrics?.current_score !== null
                    ? `${metrics?.current_score}%`
                    : 'N/A'}
                </div>
                <p className="text-xs text-muted-foreground">
                  {metrics?.score_trend_text ||
                    t('properties.details.metrics.scoreTrend')}
                </p>
              </>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('properties.details.metrics.totalAssessments')}
            </CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoadingMetrics ? (
              <div className="text-2xl font-bold text-muted-foreground">--</div>
            ) : (
              <>
                <div className="text-2xl font-bold">
                  {metrics?.total_assessments || 0}
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('properties.details.metrics.assessmentsThisMonth', {
                    count: metrics?.assessments_this_month || 0,
                  })}
                </p>
              </>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('properties.details.metrics.departments')}
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoadingMetrics ? (
              <div className="text-2xl font-bold text-muted-foreground">--</div>
            ) : (
              <>
                <div className="text-2xl font-bold">
                  {metrics?.departments_count || 0}
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('properties.details.metrics.activeDepartments')}
                </p>
              </>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('properties.details.metrics.lastAssessment')}
            </CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoadingMetrics ? (
              <div className="text-2xl font-bold text-muted-foreground">--</div>
            ) : (
              <>
                <div className="text-2xl font-bold">
                  {metrics?.last_assessment_date_formatted || 'N/A'}
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('properties.details.metrics.byInspectionTeam')}
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs - Fully Responsive */}
      <Tabs
        value={activeTab}
        onValueChange={value =>
          dispatch(
            setPropertyActiveTab(
              value as 'overview' | 'assessments' | 'departments' | 'contact'
            )
          )
        }
        className="space-y-4"
      >
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 h-auto gap-0 p-1">
          <TabsTrigger
            value="overview"
            className="text-xs sm:text-sm py-2 sm:py-2.5 px-2 sm:px-4"
          >
            {t('properties.details.tabs.overview')}
          </TabsTrigger>
          <TabsTrigger
            value="assessments"
            className="text-xs sm:text-sm py-2 sm:py-2.5 px-2 sm:px-4"
          >
            {t('properties.details.tabs.assessments')}
          </TabsTrigger>
          <TabsTrigger
            value="departments"
            className="text-xs sm:text-sm py-2 sm:py-2.5 px-2 sm:px-4"
          >
            {t('properties.details.tabs.departments')}
          </TabsTrigger>
          <TabsTrigger
            value="contact"
            className="text-xs sm:text-sm py-2 sm:py-2.5 px-2 sm:px-4"
          >
            {t('properties.details.tabs.contact')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <OverviewTab propertyId={id} TrendChartComponent={TrendChart} />
        </TabsContent>

        <TabsContent value="assessments" className="space-y-4">
          <AssessmentsTab propertyId={id} />
        </TabsContent>

        <TabsContent value="departments" className="space-y-4">
          <DepartmentsTab propertyId={id} />
        </TabsContent>

        <TabsContent value="contact" className="space-y-4">
          <ContactTab propertyId={id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
